var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, svg } from 'lit';
import { customElement, property } from 'lit/decorators.js';
/**
 * <vitesse-number-input> — themed numeric input with increment/decrement
 * spinner buttons. Reusable everywhere; size via the `size` attribute
 * (sm | md | lg). Themes automatically (light/dark) through CSS tokens.
 *
 *   <vitesse-number-input .value=${v} step="0.1" size="md"
 *       @change=${(e) => (v = e.detail.value)}></vitesse-number-input>
 *
 * `value` is kept as a STRING so intermediate states ("-", "1.", "") survive
 * typing. Emits a `change` CustomEvent<{ value: string }> on every edit. Press-
 * and-hold on a spinner button repeats (400ms delay → 75ms interval), and the
 * ArrowUp/ArrowDown keys step. Ported from the agent's NumberInput.
 */
let VitesseNumberInput = class VitesseNumberInput extends LitElement {
    constructor() {
        super(...arguments);
        this.value = '';
        this.step = 1;
        this.integer = false;
        this.disabled = false;
        this.size = 'md';
        this.placeholder = '';
    }
    static { this.styles = css `
    :host { display: block; }
    .wrap {
      display: flex;
      align-items: stretch;
      width: 100%;
      border: 1px solid var(--border-color-light);
      border-radius: var(--radius, 6px);
      background: var(--bg-primary);
      overflow: hidden;
      transition: border-color 0.18s ease;
    }
    .wrap:focus-within { border-color: var(--blue); }
    :host([disabled]) .wrap { opacity: 0.55; cursor: not-allowed; }

    input {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      color: var(--text-primary);
      outline: none;
      font: inherit;
    }
    input::placeholder { color: var(--text-secondary); }

    .btns { display: flex; flex-direction: column; border-left: 1px solid var(--border-color-light); }
    button {
      display: flex; align-items: center; justify-content: center;
      flex: 1; border: none; background: transparent;
      color: var(--text-secondary); cursor: pointer; padding: 0;
      transition: background-color 0.15s ease, color 0.15s ease;
    }
    button:hover:not(:disabled) { background: var(--hover-bg); color: var(--text-primary); }
    button:active:not(:disabled) { background: var(--hover-bg); }
    button:disabled { opacity: 0.3; cursor: not-allowed; }
    button.up { border-bottom: 1px solid var(--border-color-light); }
    button svg { width: 1em; height: 1em; }

    /* size scale — explicit heights that MATCH base-button (26 / 30 / 36), so an input and a button
       sitting in the same row are the same height instead of two-pixels-off. The vertical padding is
       zero and the height does the centring; horizontal padding stays for the text inset. */
    :host([size='sm']) .wrap { height: 26px; }
    :host([size='sm']) input { font-size: 12px; padding: 0 8px; }
    :host([size='sm']) .btns { width: 22px; }
    :host([size='md']) .wrap { height: 30px; }
    :host([size='md']) input { font-size: 14px; padding: 0 12px; }
    :host([size='md']) .btns { width: 28px; }
    :host([size='lg']) .wrap { height: 36px; }
    :host([size='lg']) input { font-size: 16px; padding: 0 14px; }
    :host([size='lg']) .btns { width: 34px; }
  `; }
    get numeric() {
        const n = parseFloat(this.value);
        return isNaN(n) ? 0 : n;
    }
    clamp(n) {
        let r = n;
        if (this.min !== undefined && r < this.min)
            r = this.min;
        if (this.max !== undefined && r > this.max)
            r = this.max;
        return this.integer ? Math.round(r) : r;
    }
    emit(v) {
        this.value = v;
        this.dispatchEvent(new CustomEvent('change', { detail: { value: v }, bubbles: true, composed: true }));
    }
    bump(dir) {
        if (this.disabled)
            return;
        const next = this.clamp(this.numeric + dir * this.step);
        // Trim trailing float noise (0.1 + 0.2 → 0.30000000000000004).
        this.emit(this.integer ? String(next) : String(Math.round(next * 1e6) / 1e6));
    }
    onInput(e) {
        this.emit(e.target.value);
    }
    onKey(e) {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.bump(1);
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.bump(-1);
        }
    }
    holdStart(dir) {
        this.bump(dir);
        this.repeatTimer = window.setTimeout(() => {
            this.repeatTimer = window.setInterval(() => this.bump(dir), 75);
        }, 400);
    }
    holdStop() {
        if (this.repeatTimer !== undefined) {
            clearTimeout(this.repeatTimer);
            clearInterval(this.repeatTimer);
            this.repeatTimer = undefined;
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.holdStop();
    }
    get atMin() { return this.min !== undefined && this.numeric <= this.min; }
    get atMax() { return this.max !== undefined && this.numeric >= this.max; }
    render() {
        const chevUp = svg `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m18 15-6-6-6 6"/></svg>`;
        const chevDown = svg `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;
        return html `
      <div class="wrap">
        <input
          type="text"
          inputmode="decimal"
          .value=${this.value}
          placeholder=${this.placeholder}
          ?disabled=${this.disabled}
          @input=${this.onInput}
          @keydown=${this.onKey}
        />
        <div class="btns">
          <button class="up" type="button" tabindex="-1" aria-label="increment"
            ?disabled=${this.disabled || this.atMax}
            @mousedown=${() => this.holdStart(1)} @mouseup=${this.holdStop} @mouseleave=${this.holdStop}>${chevUp}</button>
          <button class="down" type="button" tabindex="-1" aria-label="decrement"
            ?disabled=${this.disabled || this.atMin}
            @mousedown=${() => this.holdStart(-1)} @mouseup=${this.holdStop} @mouseleave=${this.holdStop}>${chevDown}</button>
        </div>
      </div>
    `;
    }
};
__decorate([
    property()
], VitesseNumberInput.prototype, "value", void 0);
__decorate([
    property({ type: Number })
], VitesseNumberInput.prototype, "step", void 0);
__decorate([
    property({ type: Number })
], VitesseNumberInput.prototype, "min", void 0);
__decorate([
    property({ type: Number })
], VitesseNumberInput.prototype, "max", void 0);
__decorate([
    property({ type: Boolean })
], VitesseNumberInput.prototype, "integer", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseNumberInput.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true })
], VitesseNumberInput.prototype, "size", void 0);
__decorate([
    property()
], VitesseNumberInput.prototype, "placeholder", void 0);
VitesseNumberInput = __decorate([
    customElement('vitesse-number-input')
], VitesseNumberInput);
export { VitesseNumberInput };
//# sourceMappingURL=vitesse-number-input.js.map